import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatCOP } from '../mock';
import { Calculator, TrendingDown, Wallet, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Label } from '../components/ui/label';

export default function MortgageCalculatorPage() {
  const [price, setPrice] = useState(450000000);
  const [down, setDown] = useState(30);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(11.5);

  const principal = price * (1 - down / 100);
  const r = rate / 100 / 12;
  const n = years * 12;
  const monthly = principal > 0 && r > 0 ? (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;
  const total = monthly * n;
  const interest = total - principal;

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title="Calculadora de hipoteca"
        description="Simula tu crédito hipotecario en Colombia: cuota mensual, intereses y total a pagar en segundos. Herramienta gratuita de habita.co."
      />
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Herramienta</span>
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark mt-2">Calculadora de hipoteca</h1>
          <p className="text-gray-600 mt-3 max-w-xl mx-auto">Estima tu cuota mensual y total a pagar con un crédito hipotecario en Colombia.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
            <h2 className="font-serif text-xl font-bold text-habita-dark mb-6 flex items-center gap-2"><Calculator className="w-5 h-5" /> Parámetros</h2>
            <div className="space-y-6">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Precio de la propiedad</Label>
                <Input type="number" value={price} onChange={e => setPrice(+e.target.value)} className="mt-1.5 h-12 text-lg" />
                <p className="text-sm text-habita mt-1 font-medium">{formatCOP(price)}</p>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Cuota inicial</Label>
                  <span className="text-sm font-semibold text-habita-dark">{down}% · {formatCOP(price * down / 100)}</span>
                </div>
                <input type="range" min="10" max="70" value={down} onChange={e => setDown(+e.target.value)} className="w-full accent-[#00824D]" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Plazo</Label>
                  <span className="text-sm font-semibold text-habita-dark">{years} años</span>
                </div>
                <input type="range" min="5" max="30" value={years} onChange={e => setYears(+e.target.value)} className="w-full accent-[#00824D]" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Tasa de interés</Label>
                  <span className="text-sm font-semibold text-habita-dark">{rate}% EA</span>
                </div>
                <input type="range" min="7" max="20" step="0.1" value={rate} onChange={e => setRate(+e.target.value)} className="w-full accent-[#00824D]" />
              </div>
            </div>
          </div>

          <div className="bg-habita-dark text-white rounded-3xl p-8 shadow-sm">
            <p className="text-xs uppercase tracking-widest text-amber-300 font-semibold">Resultado</p>
            <p className="text-sm text-white/70 mt-1">Cuota mensual estimada</p>
            <p className="font-serif text-5xl lg:text-6xl font-bold mt-3">{formatCOP(Math.round(monthly))}</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-white/10 rounded-xl p-4">
                <Wallet className="w-5 h-5 text-amber-300" />
                <p className="text-xs text-white/70 uppercase tracking-widest mt-2">Monto financiado</p>
                <p className="font-serif text-xl font-bold mt-1">{formatCOP(principal)}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <TrendingDown className="w-5 h-5 text-amber-300" />
                <p className="text-xs text-white/70 uppercase tracking-widest mt-2">Intereses</p>
                <p className="font-serif text-xl font-bold mt-1">{formatCOP(Math.round(interest))}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 col-span-2">
                <Calendar className="w-5 h-5 text-amber-300" />
                <p className="text-xs text-white/70 uppercase tracking-widest mt-2">Total a pagar</p>
                <p className="font-serif text-2xl font-bold mt-1">{formatCOP(Math.round(total))}</p>
              </div>
            </div>
            <p className="text-[11px] text-white/50 mt-6">* Esta calculadora ofrece un estimado. Las condiciones reales dependen de la entidad financiera.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
